import { Build, Home, Storage } from '@mui/icons-material';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DatasetIcon from '@mui/icons-material/Dataset';
import FilterListIcon from '@mui/icons-material/FilterList';
import FolderIcon from '@mui/icons-material/Folder';
import InfoIcon from '@mui/icons-material/Info';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsIcon from '@mui/icons-material/Notifications';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SavedSearchIcon from '@mui/icons-material/SavedSearch';
import React, { lazy } from 'react';

import { RequirePermission } from '../../permissions';
import { PERMISSIONS } from '../../permissions/constants';

// Every panel is lazy-loaded so route-level code splitting works, and each is
// exported here as the single source of truth. Other files that need a panel
// component only to build a `getPanelUri(<XPanel />)` lookup (never to render
// it directly) MUST import it from here rather than from the panel's own
// module — getPanelUri matches by React element `.type` identity, so a second,
// non-lazy import of the same panel would be a different reference and would
// silently fail to match (see getComponentType/getPanelIndexByComponent below).
export const AnnotationDetails = lazy(() => import('./Annotation'));
export const AnnotationFilterPanel = lazy(() => import('./AnnotationFilter'));
export const AnnotationListPanel = lazy(() => import('./AnnotationList'));
export const AnnotationManagePanel = lazy(() => import('./AnnotationManage'));
export const AnnotationSearchPanel = lazy(() => import('./AnnotationSearch'));
export const DataSetsPanel = lazy(() => import('./DataSets'));
export const DataSetsDetails = lazy(() => import('./Details'));
export const EventPanel = lazy(() => import('./Events'));
export const HomePanel = lazy(() => import('./Home'));
export const Information = lazy(() => import('./Information'));
export const MetricLabelsPanel = lazy(() => import('./MetricLabels'));
export const MetricSegmentsPanel = lazy(() => import('./MetricSegments'));
export const ProjectDetailsPanel = lazy(() => import('./ProjectDetails'));
export const ProjectPanel = lazy(() => import('./Projects'));
export const ProjectCreationPanel = lazy(() => import('./Projects/panels/ProjectCreation/ProjectCreationPanel'));
export const ProjectEditionPanel = lazy(() => import('./Projects/panels/ProjectCreation/ProjectEditPanel'));
export const QBuilderPanel = lazy(() => import('./QBuilder'));
export const BatchDetailsPanel = lazy(() => import('./Workflow/BatchDetails'));
export const BatchListPanel = lazy(() => import('./Workflow/BatchList'));
export const GuidelineDetailsPanel = lazy(() => import('./Workflow/GuidelineDetails'));
export const GuidelineListPanel = lazy(() => import('./Workflow/GuidelineList'));
export const RegistryDetailsPanel = lazy(() => import('./Workflow/RegistryDetails'));
export const RegistryListPanel = lazy(() => import('./Workflow/RegistryList'));
export const WorkflowMetrics = lazy(() => import('./WorkflowMetrics'));

// Flat panel list — used for route registration and getPanelUri lookup.
// Context-dependent detail pages (Snapshot Details, Annotation Details, Readiness Details)
// live in hiddenPanels to keep the sidebar clean.
export const panels = [
    {
        name: 'Home',
        uri: '/',
        icon: <Home />,
        component: <HomePanel />,
    },
    {
        name: 'Label Metrics',
        uri: '/metric/labels',
        icon: <AnalyticsIcon />,
        component: (
            <RequirePermission permission={PERMISSIONS.STATS_VIEW}>
                <MetricLabelsPanel />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.STATS_VIEW,
    },
    {
        name: 'Segment Metrics',
        uri: '/metric/segments',
        icon: <AssessmentIcon />,
        component: (
            <RequirePermission permission={PERMISSIONS.STATS_VIEW}>
                <MetricSegmentsPanel />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.STATS_VIEW,
    },
    {
        name: 'Workflow Metrics',
        uri: '/metric/workflow',
        icon: <QueryStatsIcon />,
        component: (
            <RequirePermission permission={PERMISSIONS.WORKFLOW_METRICS_VIEW}>
                <WorkflowMetrics />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.WORKFLOW_METRICS_VIEW,
    },
    {
        name: 'Snapshots',
        uri: '/snapshots',
        icon: <Storage />,
        component: (
            <RequirePermission permission={PERMISSIONS.SNAPSHOT_VIEW}>
                <DataSetsPanel />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.SNAPSHOT_VIEW,
    },
    {
        name: 'Snapshot Builder',
        uri: '/builder',
        icon: <Build />,
        component: (
            <RequirePermission permission={PERMISSIONS.SNAPSHOT_ADD}>
                <QBuilderPanel />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.SNAPSHOT_ADD,
    },
    {
        name: 'Annotations',
        uri: '/annotations',
        icon: <DatasetIcon />,
        component: (
            <RequirePermission permission={PERMISSIONS.ANNOTATION_VIEW}>
                <AnnotationListPanel />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.ANNOTATION_VIEW,
    },
    {
        name: 'Annotations Filter',
        uri: '/annotations/filter',
        icon: <FilterListIcon />,
        component: (
            <RequirePermission permission={PERMISSIONS.ANNOTATION_VIEW}>
                <AnnotationFilterPanel />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.ANNOTATION_VIEW,
    },
    {
        name: 'Annotations Search',
        uri: '/annotations/search',
        icon: <SavedSearchIcon />,
        component: (
            <RequirePermission permission={PERMISSIONS.ANNOTATION_VIEW}>
                <AnnotationSearchPanel />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.ANNOTATION_VIEW,
    },
    {
        name: 'Batches',
        uri: '/batches',
        icon: <FolderIcon />,
        component: (
            <RequirePermission permission={PERMISSIONS.WORKFLOW_VIEW_BATCH}>
                <BatchListPanel />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.WORKFLOW_VIEW_BATCH,
    },
    {
        name: 'Guidelines',
        uri: '/guidelines',
        icon: <MenuBookIcon />,
        component: (
            <RequirePermission permission={PERMISSIONS.WORKFLOW_VIEW_BATCH}>
                <GuidelineListPanel />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.WORKFLOW_VIEW_BATCH,
    },
    {
        name: 'Registries',
        uri: '/registries',
        icon: <LibraryBooksIcon />,
        component: (
            <RequirePermission permission={PERMISSIONS.WORKFLOW_VIEW_BATCH}>
                <RegistryListPanel />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.WORKFLOW_VIEW_BATCH,
    },
    {
        name: 'Readiness',
        uri: '/readiness',
        icon: <DashboardIcon />,
        component: (
            <RequirePermission permission={PERMISSIONS.DATAREADINESS_VIEW}>
                <ProjectPanel />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.DATAREADINESS_VIEW,
    },
    {
        annotationProcessingEvents: true,
        name: 'Events',
        uri: '/events',
        icon: <NotificationsIcon />,
        component: (
            <RequirePermission permission={PERMISSIONS.EVENT_VIEW}>
                <EventPanel />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.EVENT_VIEW,
    },
    {
        name: 'Information',
        uri: '/info',
        icon: <InfoIcon />,
        component: (
            <RequirePermission permission={PERMISSIONS.INFORMATION_VIEW}>
                <Information />
            </RequirePermission>
        ),
        requiredPermission: PERMISSIONS.INFORMATION_VIEW,
    },
];

export const hiddenPanels = [
    // More-specific static paths first for correct React Router v6 matching
    {
        name: 'New Project',
        uri: '/readiness/new',
        parent: 'Readiness',
        component: (
            <RequirePermission permission={PERMISSIONS.DATAREADINESS_VIEW}>
                <ProjectCreationPanel />
            </RequirePermission>
        ),
    },
    {
        name: 'Edit Project',
        uri: '/readiness/edit/:id',
        parent: 'Readiness',
        component: (
            <RequirePermission permission={PERMISSIONS.DATAREADINESS_VIEW}>
                <ProjectEditionPanel />
            </RequirePermission>
        ),
    },
    {
        name: 'Manage Annotation',
        uri: '/annotations/manage/:hash',
        parent: 'Annotations',
        component: (
            <RequirePermission permission={PERMISSIONS.ANNOTATION_VIEW}>
                <AnnotationManagePanel />
            </RequirePermission>
        ),
    },
    // Context-dependent detail pages (moved from panels to declutter the sidebar)
    {
        name: 'Snapshot Details',
        uri: '/snapshots/:id',
        parent: 'Snapshots',
        component: (
            <RequirePermission permission={PERMISSIONS.SNAPSHOT_VIEW}>
                <DataSetsDetails />
            </RequirePermission>
        ),
    },
    {
        id: 'annotation_details',
        name: 'Annotation Details',
        uri: '/annotations/:hash/:identifier?',
        parent: 'Annotations',
        component: (
            <RequirePermission permission={PERMISSIONS.ANNOTATION_VIEW}>
                <AnnotationDetails />
            </RequirePermission>
        ),
    },
    {
        name: 'Readiness Details',
        uri: '/readiness/:id',
        parent: 'Readiness',
        component: (
            <RequirePermission permission={PERMISSIONS.DATAREADINESS_VIEW}>
                <ProjectDetailsPanel />
            </RequirePermission>
        ),
    },
    {
        name: 'Batch Details',
        uri: '/batches/:id',
        parent: 'Batches',
        component: (
            <RequirePermission permission={PERMISSIONS.WORKFLOW_VIEW_BATCH}>
                <BatchDetailsPanel />
            </RequirePermission>
        ),
    },
    {
        name: 'Registry Details',
        uri: '/registries/:id',
        parent: 'Registries',
        component: (
            <RequirePermission permission={PERMISSIONS.WORKFLOW_VIEW_BATCH}>
                <RegistryDetailsPanel />
            </RequirePermission>
        ),
    },
    {
        name: 'Guideline Details',
        uri: '/guidelines/:id',
        parent: 'Guidelines',
        component: (
            <RequirePermission permission={PERMISSIONS.WORKFLOW_VIEW_BATCH}>
                <GuidelineDetailsPanel />
            </RequirePermission>
        ),
    },
    {
        uri: '/workflow/registries/:id',
        parent: 'Workflow',
        component: (
            <RequirePermission permission={PERMISSIONS.WORKFLOW_VIEW_BATCH}>
                <RegistryDetailsPanel />
            </RequirePermission>
        ),
    },
];

const getComponentType = (el) => (el?.props?.children ? el.props.children.type : el?.type);

const getPanelIndexByComponent = (component) => {
    if (typeof component === 'string') {
        return panels.findIndex((panel) => panel?.id === component);
    }
    // Unwrap RequirePermission (and similar) so we match the same inner type as panel.component.
    const targetType =
        typeof component === 'object' && component != null && component.type ? getComponentType(component) : component;
    return panels.findIndex((panel) => {
        const panelType = getComponentType(panel.component);
        return panelType === targetType;
    });
};

const getHiddenPanelIndexByComponent = (component) => {
    if (typeof component === 'string') {
        return hiddenPanels.findIndex((panel) => panel?.id === component);
    }
    const targetType =
        typeof component === 'object' && component != null && component.type ? getComponentType(component) : component;
    return hiddenPanels.findIndex((panel) => {
        const panelType = getComponentType(panel.component);
        return panelType === targetType;
    });
};

export const getPanelUri = (component, params = {}, extra = {}) => {
    let panelIndex = getPanelIndexByComponent(component);
    let uri = panels[panelIndex]?.uri;

    if (panelIndex === -1) {
        panelIndex = getHiddenPanelIndexByComponent(component);
        uri = hiddenPanels[panelIndex]?.uri;
    }
    if (!uri) return '';

    Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value === undefined || value === null) {
            uri = uri.replace(`/:${key}?`, '');
        } else {
            uri = uri.replace(`:${key}?`, params[key]);
            uri = uri.replace(`:${key}`, params[key]);
        }
    });

    if (uri.includes(':') && uri.includes('?')) {
        uri = uri.replace(/:[a-zA-Z_]+\??/g, '');
    }

    // Add query string parameters
    const paramsKeys = Object.keys(extra);
    if (paramsKeys.length === 0) return uri;

    uri += '?';
    paramsKeys.forEach((key) => {
        uri += `${key}=${extra[key]}&`;
    });

    uri = uri.slice(0, -1);

    return uri;
};

export const getHiddenPanelParent = (uri) => {
    // If the URI exactly matches a navigable panel it is not a hidden sub-page
    if (panels.some((p) => p.uri === uri)) return null;

    const convertToRegex = (pattern) => {
        return pattern.replace(/\/:\w+\?/g, '(/[^/]+)?').replace(/:\w+/g, '[^/]+');
    };

    const parentName = hiddenPanels.find((panel) => {
        const regexPattern = new RegExp(`^${convertToRegex(panel.uri)}$`);
        return regexPattern.test(uri);
    })?.parent;

    return panels.find((panel) => panel.name === parentName);
};

// Returns the hidden panel entry whose URI pattern matches the given path.
export const getHiddenPanelByUri = (uri) => {
    if (panels.some((p) => p.uri === uri)) return null;

    const convertToRegex = (pattern) => pattern.replace(/\/:\w+\?/g, '(/[^/]+)?').replace(/:\w+/g, '[^/]+');
    return hiddenPanels.find((panel) => {
        const regexPattern = new RegExp(`^${convertToRegex(panel.uri)}$`);
        return regexPattern.test(uri);
    });
};

// Grouped navigation structure for the Sidebar.
// Each entry is either a standalone top-level item ({ standalone, panel })
// or a collapsible group ({ groupLabel, icon, panels, requiredPermission? }).
export const panelGroups = [
    // Standalone — Home
    {
        standalone: true,
        panel: panels.find((p) => p.uri === '/'),
    },
    // Metrics
    {
        groupLabel: 'Metrics',
        icon: <AnalyticsIcon />,
        panels: panels.filter((p) => ['/metric/labels', '/metric/segments', '/metric/workflow'].includes(p.uri)),
    },
    // Snapshots
    {
        groupLabel: 'Snapshots',
        icon: <Storage />,
        panels: panels.filter((p) => ['/snapshots', '/builder'].includes(p.uri)),
    },
    // Annotations
    {
        groupLabel: 'Annotations',
        icon: <DatasetIcon />,
        panels: panels.filter((p) => ['/annotations', '/annotations/filter', '/annotations/search'].includes(p.uri)),
    },
    // Workflow
    {
        groupLabel: 'Workflow',
        icon: <FolderIcon />,
        requiredPermission: PERMISSIONS.WORKFLOW_VIEW_BATCH,
        panels: panels.filter((p) => p.requiredPermission === PERMISSIONS.WORKFLOW_VIEW_BATCH),
    },
    // Readiness
    {
        groupLabel: 'Readiness',
        icon: <DashboardIcon />,
        panels: panels.filter((p) => p.uri === '/readiness'),
    },
    // Standalone — Events
    {
        standalone: true,
        panel: panels.find((p) => p.uri === '/events'),
    },
    // Standalone — Information
    {
        standalone: true,
        panel: panels.find((p) => p.uri === '/info'),
    },
];
